// src/encounter/lib/fhir-to-ds-questionnaire.ts
// Type bridge: the BFF returns the local `FhirQuestionnaire` (nullable wire fields,
// `item.type: string`), but the DS `QuestionnaireRenderer` consumes the DS
// `Questionnaire` (`item` required, no nulls, `type: QItemType`). This pure normalizer
// recursively maps the item tree into the DS shape.
//
// PROD-1494 — the DYNAMIC-TEMPLATE mechanisms now pass through to the DS engine
// (PROD-1486), each read from its first-class field OR its FHIR/SDC extension form:
//  • enableWhen — operands normalized to the DS scalar: `answerCoding` → the renderer's
//    option key (`code ?? display`); string/boolean/number pass as-is. + enableBehavior.
//  • calculatedExpression — first-class (post-804 `ExpressionJson`) or the SDC
//    `sdc-questionnaire-calculatedExpression` item extension. + `variable` extensions.
//  • readOnly.
//  • required — the authored MANDATORY flag. Never read before, so no template has ever
//    drawn a marker in the encounter; the DS `QItem.required` has always existed and the
//    renderer already draws it as a trailing ` *` on choice/open-choice/attachment labels
//    (DS 1.60.0 does NOT yet mark boolean/string/numeric/text/quantity/date — a DS change,
//    see required-marker.render.test.tsx) (PROD-2039).
//  • answerOption `ordinalValue` — first-class or the base FHIR option extension
//    (valueDecimal/valueInteger) — required for scoring expressions (PHQ-9 et al).
//  • HOIST: children of NON-GROUP items are lifted to following siblings — the DS
//    renders (and flattens for evaluation) children of groups only, while FHIR allows
//    children under any item (PROD-1487: an imported cascade was invisible until
//    hoisted). Document order is preserved; group-only templates are unaffected.
// It does NOT validate `type` against the QItemType union — the BFF authors FHIR item
// types and the DS renderer ignores any it doesn't dispatch.
import { SDC_INITIAL_EXPRESSION_URL, type PopulatedItem } from './chart-population';
import type { Questionnaire, QItem, QItemType, QEnableWhen } from '@scripsteam/scrips-signal-ds';
import { applyLayoutSpans, asItemControlRole } from '@scripsteam/scrips-signal-ds';
import {
  SDC_CALCULATED_EXPRESSION_URL,
  FHIR_VARIABLE_URL,
  ORDINAL_VALUE_URL,
  ITEM_CONTROL_URL,
  QUESTIONNAIRE_UNIT_URL,
  type FhirQuestionnaire,
  type FhirQuestionnaireItem,
  type FhirAnswerOption,
  type FhirCoding,
  type FhirEnableWhen,
} from '@/types/template-studio-api';
import { isCpsLayoutExtension } from './cps-layout';

// QAnswerOption isn't re-exported from the package index — derive it from QItem.
type QAnswerOption = NonNullable<QItem['answerOption']>[number];

function toDsAnswerOption(opt: FhirAnswerOption): QAnswerOption {
  const coding = opt.valueCoding;
  const out: QAnswerOption = coding
    ? {
        valueCoding: {
          ...(coding.code != null ? { code: coding.code } : {}),
          ...(coding.display != null ? { display: coding.display } : {}),
          ...(coding.system != null ? { system: coding.system } : {}),
        },
      }
    : {};
  // Ordinal weight: first-class wins; else the base FHIR `ordinalValue` option extension.
  const ordinal =
    opt.ordinalValue ??
    opt.extension?.find((e) => e.url === ORDINAL_VALUE_URL)?.valueDecimal ??
    opt.extension?.find((e) => e.url === ORDINAL_VALUE_URL)?.valueInteger;
  if (ordinal != null) out.ordinalValue = ordinal;
  return out;
}

/** enableWhen operand → the DS scalar. A coding compares against the renderer's stored
 *  option key (`code ?? display` — its `optionValue`), so map codings to that key. */
function toDsEnableWhen(cond: FhirEnableWhen): QEnableWhen {
  const answer =
    cond.answerCoding != null
      ? (cond.answerCoding.code ?? cond.answerCoding.display ?? undefined)
      : (cond.answerBoolean ?? cond.answerString ?? cond.answerInteger ?? cond.answerDecimal ?? undefined);
  return {
    question: cond.question,
    operator: cond.operator as QEnableWhen['operator'],
    ...(answer !== undefined ? { answer } : {}),
  };
}

/**
/**
 * `PaQItem` IS GONE (Q2, 2026-09-16), and this note is here so it is not reinvented.
 *
 * It was `QItem & { itemControlCode?: string }` — a PA-only carrier for the RAW authored
 * itemControl code. It existed because signal-ds 1.63.0 closed `QItem.itemControl` to the roles
 * the DS renderer draws, and `chief-complaint-hpi` was not one of them: the Studio stamped it, the
 * DS dropped it, so the PA kept its own copy to find the marker with.
 *
 * Samer's Q2 ruling removed the premise. The chief-complaint block is a real Signal DS component
 * now and the code is a real DS role, so `asItemControlRole` narrows it like any other and it
 * arrives on `QItem.itemControl`. Nothing read `itemControlCode` except the marker detector.
 *
 * If a FUTURE code needs to survive the narrowing, the answer is a DS role and a component, not a
 * second vocabulary in the consumer — that was the duplication this file exists to end.
 */

/** One FHIR item → one-or-more DS items: itself, then any HOISTED non-group children. */
function toDsItems(item: FhirQuestionnaireItem): QItem[] {
  const out: QItem = {
    linkId: item.linkId,
    // The BFF carries arbitrary FHIR item types; the DS union is the documented set.
    // The renderer dispatch-table ignores unknown types, so a narrowing cast is safe here.
    type: item.type as QItemType,
  };
  if (item.text != null) out.text = item.text;
  if (item.repeats != null) out.repeats = item.repeats;
  // REQUIRED — the authored mandatory flag. First-class FHIR, guarded like repeats/readOnly:
  // a wire `false` rides through as `false`, an absent/null one is omitted. Both read as "not
  // required" downstream; neither invents a default (PROD-2039).
  if (item.required != null) out.required = item.required;
  if (item.readOnly != null) out.readOnly = item.readOnly;
  // TERMINOLOGY BINDINGS — FHIR first-class `item.code`, carried through to `QItem.code`
  // (PROD-2351). Not decorative: the ROS/PE rail abbreviation rides a `cps-abbreviation`
  // coding here, and `railAbbreviation` (questionnaire-groups.ts) reads it off the MAPPED
  // item. Dropping it here left every Studio-authored rail chip falling back to the group's
  // full text ("Genitourinary (female)" where the baseline rail reads "GU(F)") — the helper
  // and the call site were both correct and nothing connected them. Mirrors the DS's own
  // `fhir-to-qitem` contract: an entry without a `code` string is a half-binding and is
  // dropped rather than handed on; null `system`/`display` become absent, never null.
  const codes = (item.code ?? [])
    .filter((c): c is FhirCoding & { code: string } => typeof c?.code === 'string' && c.code !== '')
    .map((c) => ({
      code: c.code,
      ...(c.system != null ? { system: c.system } : {}),
      ...(c.display != null ? { display: c.display } : {}),
    }));
  if (codes.length > 0) out.code = codes;
  if (item.answerOption != null) out.answerOption = item.answerOption.map(toDsAnswerOption);
  // `item.initial` — carried the way the DS's own `fhir-to-qitem.ts` carries it: `valueBoolean`
  // only, entries without one dropped. The renderer reads it as a switch's resting state
  // (`qrInitialBoolean`) — without it an authored "Fall risk present: on" rendered off.
  const initial = (item.initial ?? [])
    .filter((i): i is { valueBoolean: boolean } => typeof i?.valueBoolean === 'boolean')
    .map((i) => ({ valueBoolean: i.valueBoolean }));
  if (initial.length > 0) out.initial = initial;
  if (item.enableWhen != null && item.enableWhen.length > 0) {
    out.enableWhen = item.enableWhen.map(toDsEnableWhen);
    if (item.enableBehavior === 'any' || item.enableBehavior === 'all') {
      out.enableBehavior = item.enableBehavior;
    }
  }
  // Computed field: first-class carrier wins; else the SDC item extension.
  const calc =
    item.calculatedExpression ??
    item.extension?.find((e) => e.url === SDC_CALCULATED_EXPRESSION_URL)?.valueExpression?.expression;
  if (calc != null) out.calculatedExpression = calc;
  // UNIT — the SDC `questionnaire-unit` extension's UCUM code, surfaced as the DS
  // `QItem.unit` the renderer already draws as a trailing adornment on a numeric
  // TextField. Never populated before, so every quantity question rendered as a
  // bare number box: a clinician entering HbA1c saw no `%`, and a glucose no
  // mg/dL-vs-mmol/L. The CODE is used, not the display — UCUM codes ARE the
  // human-facing symbols (`mg/dL`, `mm[Hg]`), and `display` is optional on the wire.
  const unit = item.extension?.find((e) => e.url === QUESTIONNAIRE_UNIT_URL)?.valueCoding?.code;
  if (unit != null && unit !== '') out.unit = unit;
  // Presentation hint (DS-033): the FHIR `questionnaire-itemControl` extension's coding code
  // (e.g. 'symptom-chip' → tri-state SymptomChip on a boolean). `asItemControlRole` is the
  // BOUNDARY: it narrows a code the DS publishes as a role and drops anything else to
  // `undefined`, i.e. the documented default rendering. Wire data stays untrusted.
  //
  // THIS USED TO BE SPLIT IN TWO and is not any more (Q2, 2026-09-16). A second field,
  // `itemControlCode`, carried the code VERBATIM so a PA-side marker outside the DS vocabulary —
  // `chief-complaint-hpi` — could still be found. That block is a Signal DS component now and the
  // code is a DS role, so there is nothing left outside the vocabulary for the PA to track.
  //
  // 9-vs-7, as of signal-ds 1.66.0. `ITEM_CONTROL_ALLOWLIST` (what QuestionBank accepts on the
  // wire) is NINE codes: symptom-chip slider drawing photo callout-info callout-warning richtext
  // readout chief-complaint-hpi. `ITEM_CONTROL_ROLES` (what the renderer actually draws) is seven
  // of them — `richtext` and `readout` are allowlisted but have no component yet, which the DS's
  // own catalogue doc states outright. Their blocker is a DS renderer branch, NOT the backend:
  // QuestionBank has accepted both since #151 (2026-09-10), measured by Andrew against the service
  // source 2026-09-16. So a Studio template authoring a richtext / readout round-trips through the
  // wire intact and renders here as a DEFAULT item until the DS gives those two a role — no error,
  // by design (an older client must not break on a newer template). Deliberately NOT pinned by a
  // test: such a test would flip red on the day the DS ships the remaining roles.
  const itemControl = item.extension
    ?.find((e) => e.url === ITEM_CONTROL_URL)
    ?.valueCodeableConcept?.coding?.find((c) => c.code != null)?.code;
  if (itemControl != null) {
    const role = asItemControlRole(itemControl);
    if (role != null) out.itemControl = role;
  }
  // Named FHIRPath variables (`%name`) — the FHIR `variable` extension.
  const variables = (item.extension ?? [])
    .filter((e) => e.url === FHIR_VARIABLE_URL)
    .map((e) => e.valueExpression)
    .filter((v): v is NonNullable<typeof v> => v?.name != null && v.expression != null)
    .map((v) => ({ name: v.name as string, expression: v.expression as string }));
  if (variables.length > 0) out.variable = variables;
  // PROD-3040 · SDC expression-based population (`sdc-questionnaire-initialExpression`): carried for the frame v2
  // note, which fills the item from the chart and draws it read-only (lib/chart-population.ts). The DS renderer
  // does not read it, so the switch-OFF render is unchanged.
  const initialExpression = item.extension?.find((e) => e.url === SDC_INITIAL_EXPRESSION_URL)?.valueExpression?.expression;
  if (typeof initialExpression === 'string' && initialExpression.trim()) (out as PopulatedItem).initialExpression = initialExpression.trim();

  const children = (item.item ?? []).flatMap(toDsItems);
  if (item.type === 'group') {
    out.item = children;
    return [out];
  }
  // HOIST: the DS renders children of groups only — lift a question's children to
  // siblings right after it (document order preserved; enableWhen on them still works).
  return [out, ...children];
}

/* THE `cps-layout` EXTENSION — background for the layout read at the bottom of this file.
 *
 * Template Studio's layout document, carried as a JSON string in an extension on the
 * Questionnaire ROOT. It holds the page/section/column structure an author built — and it
 * is the ONLY place that structure exists, because the FHIR item tree is flat: a four-cell
 * Grid emits four sibling items and no container.
 *
 * This app has always been handed the document and never opened it, which is why a template
 * authored 4-across rendered as four stacked rows here. Measured, not assumed: template
 * e6dd8e47 on dev carries an hstack at span 6/6 and a grid at span 3, and every hop —
 * store, round-trip, delivery to this endpoint — was verified before this line existed.
 *
 * MATCHED THROUGH THE ONE SHARED MATCHER (PROD-2129) — `isCpsLayoutExtension` in cps-layout.ts.
 * This site used to compare `e.url` against a hard-coded full StructureDefinition URL, while the
 * step-title reader on the same extension accepted the short form too; a template delivered under
 * the short url kept its authored tab titles and lost its columns. One rule now, both readers.
 */

export function fhirToDsQuestionnaire(q: FhirQuestionnaire): Questionnaire {
  const base: Questionnaire = {
    resourceType: 'Questionnaire',
    ...(q.title != null ? { title: q.title } : {}),
    item: (q.item ?? []).flatMap(toDsItems),
  };
  /* Spans are applied by the design system, not here, because Template Studio's Preview
   * needs the identical mapping and the two apps cannot import each other. A private copy
   * on each side is exactly how Preview came to disagree with this adapter about units,
   * itemControl, enableWhen and option scores.
   *
   * Safe to call unconditionally: with no layout — every template authored before the
   * Studio, and every one written through the API — it returns `base` by the same
   * reference. A malformed document does the same. The columns are lost, never the
   * questions. */
  const layoutJson = (q.extension ?? []).find((e) => isCpsLayoutExtension(e))?.valueString;
  return applyLayoutSpans(base, layoutJson);
}
